import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { api, getAll } from './client';
import type {
  Actuation, Config, Device, GeofencingEvent, GeofencingSource, Health, LongAbsence, Measurement,
  OccupancyInterval, OccupancySummary, Override, Page, Presence, Reading, Room, Routine, SamplePage,
  State, StateHistoryEntry, Wake, WakeDeclaration,
} from './types';

type Opts<T> = Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'>;

export const qk = {
  health: ['health'] as const,
  config: ['config'] as const,
  rooms: ['rooms'] as const,
  devices: ['devices'] as const,
  device: (id: string) => ['devices', id] as const,
  actuation: (id: string) => ['devices', id, 'actuation'] as const,
  readings: (id: string) => ['sensors', id, 'readings'] as const,
  state: ['state'] as const,
  stateHistory: (page: number) => ['state', 'history', page] as const,
  routines: ['routines'] as const,
  routine: (id: string) => ['routines', id] as const,
  presence: ['presence'] as const,
  sources: ['geofencing', 'sources'] as const,
  geoEvents: (page: number) => ['geofencing', 'events', page] as const,
  occupancy: (groupBy: string) => ['occupancy', 'summary', groupBy] as const,
  occupancyHistory: ['occupancy', 'history'] as const,
  wakes: (status: string) => ['wakes', status] as const,
  wakeHistory: ['wakes', 'history'] as const,
  absences: (status: string) => ['absences', status] as const,
  overrides: (active: boolean) => ['overrides', active] as const,
  measurementStatus: ['measurement', 'status'] as const,
  measurementResults: ['measurement', 'results'] as const,
  measurement: (id: string) => ['measurement', 'results', id] as const,
  samples: (id: string) => ['measurement', 'results', id, 'samples'] as const,
};

export const useHealth = (o?: Opts<Health>) =>
  useQuery({ queryKey: qk.health, queryFn: () => api.get<Health>('/health'), refetchInterval: 60_000, ...o });

export const useConfig = () => useQuery({ queryKey: qk.config, queryFn: () => api.get<Config>('/config') });

export const useRooms = () => useQuery({ queryKey: qk.rooms, queryFn: () => getAll<Room>('/rooms') });

export const useDevices = () =>
  useQuery({ queryKey: qk.devices, queryFn: () => getAll<Device>('/devices'), refetchInterval: 60_000 });

export const useActuation = (id: string | null) =>
  useQuery({
    queryKey: qk.actuation(id ?? ''),
    queryFn: () => api.get<Page<Actuation>>(`/devices/${id}/actuation`, { limit: 10 }),
    enabled: !!id,
  });

export const useReadings = (id: string | null) =>
  useQuery({
    queryKey: qk.readings(id ?? ''),
    queryFn: () => api.get<Page<Reading>>(`/sensors/${id}/readings`, { limit: 10 }),
    enabled: !!id,
  });

/** GET /state avec If-None-Match ; 5 s pendant une transition matérielle, 30 s sinon (REQ-17). */
export const useStates = () =>
  useQuery({
    queryKey: qk.state,
    queryFn: () => getAll<State>('/state'),
    refetchInterval: (q) => {
      const rows = q.state.data ?? [];
      return rows.some((s) => s.command_status === 'pending' || s.command_status === 'applying') ? 5_000 : 30_000;
    },
  });

export const useStateHistory = (page = 1, limit = 20) =>
  useQuery({
    queryKey: qk.stateHistory(page),
    queryFn: () => api.get<Page<StateHistoryEntry>>('/state/history', { page, limit }),
  });

/** Liste des routines, détaillées une à une (phases + assignations + ETag). */
export const useRoutines = () =>
  useQuery({
    queryKey: qk.routines,
    queryFn: async () => {
      const list = await getAll<Routine>('/routines');
      return Promise.all(list.map((r) => api.get<Routine>(`/routines/${r.id}`)));
    },
  });

export const usePresence = () =>
  useQuery({ queryKey: qk.presence, queryFn: () => api.get<Presence>('/presence'), refetchInterval: 30_000 });

export const useSources = () => useQuery({ queryKey: qk.sources, queryFn: () => getAll<GeofencingSource>('/geofencing/sources') });

export const useGeoEvents = (page = 1) =>
  useQuery({ queryKey: qk.geoEvents(page), queryFn: () => api.get<Page<GeofencingEvent>>('/geofencing/events', { page, limit: 20 }) });

export const useOccupancySummary = (groupBy: 'day' | 'week' | 'month', from: string) =>
  useQuery({
    queryKey: [...qk.occupancy(groupBy), from],
    queryFn: () => api.get<OccupancySummary>('/occupancy/summary', { group_by: groupBy, from }),
  });

export const useOccupancyHistory = () =>
  useQuery({ queryKey: qk.occupancyHistory, queryFn: () => api.get<Page<OccupancyInterval>>('/occupancy/history', { limit: 10 }) });

export const useWakes = (status: 'scheduled' | 'triggered' | 'cancelled', limit = 20) =>
  useQuery({
    queryKey: qk.wakes(status),
    queryFn: () => api.get<Page<Wake>>('/events/wake', { status, limit }),
    refetchInterval: status === 'scheduled' ? 60_000 : false,
  });

export const useWakeHistory = () =>
  useQuery({ queryKey: qk.wakeHistory, queryFn: () => api.get<Page<WakeDeclaration>>('/events/wake/history', { limit: 10 }) });

export const useAbsences = (status: 'scheduled' | 'active' | 'ended' | 'cancelled') =>
  useQuery({ queryKey: qk.absences(status), queryFn: () => getAll<LongAbsence>('/long-absences', { status }) });

export const useOverrides = (active: boolean) =>
  useQuery({
    queryKey: qk.overrides(active),
    queryFn: () => api.get<Page<Override>>('/overrides', { active, limit: active ? 100 : 10 }),
    refetchInterval: active ? 30_000 : false,
  });

export const useMeasurementStatus = () =>
  useQuery({
    queryKey: qk.measurementStatus,
    queryFn: () => api.get<Measurement | null>('/measurement/status'),
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 30_000 : 120_000),
  });

export const useMeasurementResults = () =>
  useQuery({ queryKey: qk.measurementResults, queryFn: () => api.get<Page<Measurement>>('/measurement/results', { limit: 20 }) });

export const useMeasurement = (id: string | null) =>
  useQuery({ queryKey: qk.measurement(id ?? ''), queryFn: () => api.get<Measurement>(`/measurement/results/${id}`), enabled: !!id });

/** Jusqu'à 1 500 échantillons en un appel (REQ-22). */
export const useSamples = (id: string | null, live = false) =>
  useQuery({
    queryKey: qk.samples(id ?? ''),
    queryFn: () => api.get<SamplePage>(`/measurement/results/${id}/samples`, { limit: 1500 }),
    enabled: !!id,
    refetchInterval: live ? 60_000 : false,
  });
